import type { FiltersState } from '../../hooks/useFilters';
import type { SearchApiParams, SearchQuery, SourceFilterAdapter } from '../filterAdapters/types';
import { normalizeAvitoExperienceRange } from './experienceRange';

const setIf = (target: SearchQuery, key: string, value: unknown) => {
  if (value === undefined || value === null) return;
  if (typeof value === 'string' && value.trim() === '') return;
  target[key] = value;
};

const setArray = (target: SearchQuery, key: string, value: unknown) => {
  if (!Array.isArray(value) || value.length === 0) return;
  target[key] = value;
};

const setParam = (target: SearchApiParams, key: string, value: unknown) => {
  if (value === undefined || value === null) return;
  const str = String(value).trim();
  if (str === '') return;
  target[key] = str;
};

const setCsvParam = (target: SearchApiParams, key: string, list: unknown) => {
  if (!Array.isArray(list) || list.length === 0) return;
  const csv = list.map((value) => String(value).trim()).filter(Boolean).join(',');
  if (csv) target[key] = csv;
};

const toAvitoLocationCsv = (cityIds: unknown): string => {
  const rawList = Array.isArray(cityIds) ? cityIds : [cityIds];
  return rawList
    .filter((value) => value !== undefined && value !== null)
    .flatMap((value) => String(value).split(','))
    .map((value) => value.trim().split('/').pop()?.trim())
    .filter((value): value is string => Boolean(value))
    .join(',');
};

export const avitoFilterAdapter: SourceFilterAdapter = {
  source: 'avito',

  toSearchQuery(filters: FiltersState): SearchQuery {
    const experience = normalizeAvitoExperienceRange(filters);
    const query: SearchQuery = {
      source: filters.source,
      sortBy: filters.sortBy,
      page: filters.page,
      pageSize: filters.limit || 20,
    };

    setIf(query, 'salaryFrom', filters.salaryFrom);
    setIf(query, 'salaryTo', filters.salaryTo);
    if (filters.incomeSpecified) query.incomeSpecified = true;
    if (filters.hasSalary) query.hasSalary = true;
    setIf(query, 'ageFrom', filters.ageFrom);
    setIf(query, 'ageTo', filters.ageTo);
    setIf(query, 'gender', filters.gender);
    setArray(query, 'citizenship', filters.citizenship);
    setArray(query, 'cityIds', filters.cityIds);
    setArray(query, 'cities', filters.cities);
    setIf(query, 'positionName', filters.positionName);
    if (filters.strictSearch) query.strictSearch = true;
    setArray(query, 'avitoKeywords', filters.avitoKeywords);
    setArray(query, 'avitoExcludedWords', filters.avitoExcludedWords);
    if (filters.scoringEnabled === false) query.scoringEnabled = false;
    setArray(query, 'selectedSpecializations', filters.selectedSpecializations);
    setArray(query, 'avitoEducationLevel', filters.avitoEducationLevel);
    setArray(query, 'avitoSchedule', filters.avitoSchedule);
    setArray(query, 'avitoBusinessTrip', filters.avitoBusinessTrip);
    setArray(query, 'avitoRelocation', filters.avitoRelocation);
    setIf(query, 'avitoExperienceMin', experience.avitoExperienceMin);
    setIf(query, 'avitoExperienceMax', experience.avitoExperienceMax);
    setIf(query, 'avitoDriverLicence', filters.avitoDriverLicence);
    setArray(query, 'avitoDriverLicenceCategory', filters.avitoDriverLicenceCategory);
    setArray(query, 'avitoDrivingExperience', filters.avitoDrivingExperience);
    setArray(query, 'avitoOwnTransport', filters.avitoOwnTransport);
    setIf(query, 'avitoMedicalBook', filters.avitoMedicalBook);
    setIf(query, 'avitoUpdatedFrom', filters.avitoUpdatedFrom);
    setIf(query, 'avitoUpdatedTo', filters.avitoUpdatedTo);

    return query;
  },

  toApiParams(filters: SearchQuery): SearchApiParams {
    const experience = normalizeAvitoExperienceRange(filters as Partial<FiltersState>);
    const params: SearchApiParams = {
      page: filters?.page || 1,
      per_page: filters?.pageSize || filters?.limit || 20,
    };

    setParam(params, 'position_name', filters?.positionName);
    if (filters?.strictSearch) {
      params.strictSearch = 'true';
    }
    if (filters?.avitoCursor !== undefined && filters?.avitoCursor !== null && String(filters.avitoCursor).trim() !== '') {
      const avitoCursor = Number(filters.avitoCursor);
      if (Number.isFinite(avitoCursor)) {
        params.avitoCursor = avitoCursor;
      }
    }
    setCsvParam(params, 'avito_keywords', filters?.avitoKeywords);
    setCsvParam(params, 'avito_excluded_words', filters?.avitoExcludedWords);

    const location = toAvitoLocationCsv(filters?.cityIds);
    if (location) params.location = location;

    setCsvParam(params, 'specialization', filters?.selectedSpecializations);
    setParam(params, 'age_min', filters?.ageFrom);
    setParam(params, 'age_max', filters?.ageTo);
    setParam(params, 'salary_min', filters?.salaryFrom);
    setParam(params, 'salary_max', filters?.salaryTo);

    if (filters?.hasSalary || filters?.incomeSpecified) {
      params.has_salary = 'true';
    }

    if (filters?.gender === 'M' || filters?.gender === 'male') {
      params.gender = 'male';
    } else if (filters?.gender === 'F' || filters?.gender === 'female') {
      params.gender = 'female';
    }

    setCsvParam(params, 'nationality', filters?.citizenship);
    setCsvParam(params, 'education_level', filters?.avitoEducationLevel);
    setCsvParam(params, 'schedule', filters?.avitoSchedule);
    setCsvParam(params, 'business_trip_readiness', filters?.avitoBusinessTrip);
    setCsvParam(params, 'relocation_readiness', filters?.avitoRelocation);
    setParam(params, 'experience_min', experience.avitoExperienceMin);
    setParam(params, 'experience_max', experience.avitoExperienceMax);
    setParam(params, 'driver_licence', filters?.avitoDriverLicence);
    setCsvParam(params, 'driver_licence_category', filters?.avitoDriverLicenceCategory);
    setCsvParam(params, 'driving_experience', filters?.avitoDrivingExperience);
    setCsvParam(params, 'own_transport', filters?.avitoOwnTransport);
    setParam(params, 'medical_book', filters?.avitoMedicalBook);
    setParam(params, 'updated_from', filters?.avitoUpdatedFrom);
    setParam(params, 'updated_to', filters?.avitoUpdatedTo);

    return params;
  },
};
